import { makeVar } from '@apollo/client';

/**
 * Global state for the network-timeout takeover screen.
 *
 * When a non-exempt GraphQL operation times out, the Apollo `onError` link lets
 * the error through to the component as usual and records the timeout here,
 * which shows the screen. The route the user was on is captured at that moment.
 *
 * The TimeoutScreen reads `timeoutVisibleVar`. Its Retry button calls
 * `retryTimeout`, which hides the screen and bumps `routeResetVar` — used as the
 * `key` on `<Routes />` — so the whole route subtree remounts and re-runs its
 * queries. The default fetch policy in `gqlPolicesUtil` is `network-only`, so a
 * remount is enough to re-hit the network; no explicit refetch is needed.
 *
 * Retry intentionally does NOT touch auth/session logic — genuine auth failures
 * surface separately as FORBIDDEN and go through `handleForbiddenError`.
 *
 * Retries are capped per operation. Filter state lives in reactive vars that
 * survive a remount, so an operation failing because of the current filter
 * selection would otherwise time out again immediately and trap the user behind
 * a takeover screen with no way out. Once an operation has spent its budget the
 * error falls through to the ordinary error notification instead.
 */

/** How many times a single operation may trigger the takeover screen. */
export const MAX_TIMEOUT_RETRIES = 1;

export const timeoutVisibleVar = makeVar<boolean>(false);

/** Bumped on retry; used as the `key` on `<Routes />` to force a remount. */
export const routeResetVar = makeVar<number>(0);

let timedOutRoute: string | null = null;
let timeoutCounts: Record<string, number> = {};

const currentRoute = (): string => `${window.location.pathname}${window.location.search}`;

/**
 * Record a timed-out operation.
 *
 * Returns `true` when the takeover screen is showing and the caller should
 * swallow the error, `false` when this operation has already spent its retry
 * budget and the caller should surface the error normally instead.
 */
export const registerTimeout = (operationName?: string): boolean => {
  const key = operationName || 'unknown';
  const previousTimeouts = timeoutCounts[key] ?? 0;

  timeoutCounts[key] = previousTimeouts + 1;

  if (previousTimeouts >= MAX_TIMEOUT_RETRIES) {
    return false;
  }

  if (timedOutRoute === null) {
    timedOutRoute = currentRoute();
  }
  timeoutVisibleVar(true);

  return true;
};

/**
 * Forget an operation's timeout history once it succeeds, so a later unrelated
 * blip still gets its own retry instead of going straight to a notification.
 */
export const clearTimeoutCount = (operationName?: string): void => {
  delete timeoutCounts[operationName || 'unknown'];
};

/**
 * Hide the screen and trigger the route remount. Returns the route that was
 * captured when the timeout happened, so the caller can navigate back to it if
 * the user has since moved, or `null` if there is nothing to return to.
 */
export const retryTimeout = (): string | null => {
  const route = timedOutRoute;

  timedOutRoute = null;
  timeoutVisibleVar(false);
  routeResetVar(routeResetVar() + 1);

  return route;
};

/** Drop all timeout state without retrying (e.g. on logout). */
export const clearPendingTimeouts = (): void => {
  timedOutRoute = null;
  timeoutCounts = {};
  timeoutVisibleVar(false);
};

/** Test helper: the route captured when the current timeout was registered. */
export const getTimedOutRoute = (): string | null => timedOutRoute;

/** Test helper: how many times an operation has timed out. */
export const getTimeoutCount = (operationName?: string): number => timeoutCounts[operationName || 'unknown'] ?? 0;
